// Legacy: CumplimientoSinOcViewImpl.java -> handler saCumplimientoSinOCMasivo
// Legacy: CargaArchivoActivity.java -> modal de carga reutilizable
// Legacy: LectorCumplimientosSinOC.java -> parseo y validación del Excel CumplimientoSinOC.xls
import {
  Component,
  inject,
  signal,
  computed,
  input,
  output,
  effect,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import {
  FileUploaderComponent,
  FileSelectedEvent
} from '../shared/file-uploader/file-uploader.component';
import { CargaMasivaRepository } from 'src/app/domain/repository/carga-masiva.repository';
import {
  CargaMasivaResumen,
  FilaResultado
} from 'src/app/domain/model/carga-masiva/carga-masiva';
import { cumplimientosMasivosResultadoPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * CumplimientosMasivasDialogComponent
 *
 * Diálogo para el cargue masivo de cumplimientos Sin Orden de Compra
 * desde un archivo Excel (.xls).
 *
 * Replica el comportamiento del legacy GWT:
 * - Botón `saCumplimientoSinOCMasivo` en CumplimientoSinOcViewImpl.java
 * - Modal genérico de carga de archivo CargaArchivoActivity.java
 * - Lector LectorCumplimientosSinOC.java (ClaseLectorExcel.LECTOR_CUMPLIMIENTOS_SINOC)
 *
 * Estructura del Excel CumplimientoSinOC.xls (23 columnas):
 * Col 0:  Oficina (nombre)
 * Col 1:  Contrato
 * Col 2:  Código Centro Trabajo
 * Col 3:  Código Tarea
 * Col 4:  Código Producto
 * Col 5:  Tipo de Cita (solo "CAMPO")
 * Col 6:  Clasificación (solo "ASESORÍA")
 * Col 7:  Forma Entrega
 * Col 8:  Tipo de llamada (requerido solo si TELEFONICO)
 * Col 9:  Canal de servicio
 * Col 10: Código Municipio Origen
 * Col 11: Código Municipio Entrega
 * Col 12: Dni Asesor
 * Col 13: Dni Aseguradora
 * Col 14: Fecha Programación (yyyy-MM-dd HH:mm:ss)
 * Col 15: Cantidad Ejecutada
 * Col 16: Contacto (opcional)
 * Col 17: Email Contacto (opcional)
 * Col 18: Pago Transporte Empleado (S/N, opcional)
 * Col 19: Valor Transporte (opcional)
 * Col 20: Pago Transporte Adicional (S/N, opcional)
 * Col 21: Valor Transporte Adicional (opcional)
 * Col 22: Observaciones Transporte Adicional (opcional)
 *
 * @author GIROP Migration
 */
@Component({
  selector: 'app-cumplimientos-masivos-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    MessageModule,
    TooltipModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    FileUploaderComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Cumplimientos Masivos"
      [visible]="dialogVisible()"
      [modal]="true"
      [closable]="!procesando()"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '92vw', maxWidth: '64rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      (visibleChange)="onDialogVisibleChange($event)"
    >
      <!-- Estado: Carga de archivo -->
      @if (estado() === 'cargar') {
        <div class="mb-4">
          <p class="text-gray-600 dark:text-gray-300 mb-4">
            Seleccione un archivo Excel (.xls o .xlsx) con los datos de los
            cumplimientos a registrar. El archivo debe seguir la estructura de
            la plantilla <strong>CumplimientoSinOC.xls</strong>.
          </p>

          <!-- Legacy: CargaArchivoActivity -> getSaPlantilla() descarga CumplimientoSinOC.xls -->
          <app-file-uploader
            #fileUploader
            [templateUrl]="plantillaUrl"
            [templateFileName]="'CumplimientoSinOC.xls'"
            [maxFileSize]="10 * 1024 * 1024"
            (onFileSelected)="onFileSelected($event)"
            (onFileRemoved)="onFileRemoved()"
            (onError)="onFileError($event)"
          >
          </app-file-uploader>
        </div>

        <!-- Mensaje de error inline -->
        @if (error()) {
          <p-message
            severity="error"
            [text]="error()!"
            styleClass="mt-4 w-full"
          >
          </p-message>
        }

        <!-- Panel de resultados -->
        @if (resultado()) {
          <div class="mt-4">
            <!-- Resumen de contadores -->
            <!-- Legacy: CargaArchivoActivity -> panel estado (total / exitosos / error) -->
            <div
              class="mb-4 p-4 rounded-lg"
              [class]="
                resultado()!.filasConError === 0
                  ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700'
                  : 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700'
              "
            >
              <div class="flex items-center gap-3 mb-3">
                <i
                  [class]="
                    resultado()!.filasConError === 0
                      ? 'pi pi-check-circle text-green-600 text-2xl'
                      : 'pi pi-exclamation-triangle text-yellow-600 text-2xl'
                  "
                ></i>
                <h4 class="text-lg font-semibold m-0 dark:text-gray-100">
                  {{ resultado()!.mensaje }}
                </h4>
              </div>

              <div
                class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center mt-4"
              >
                <div class="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <p
                    class="text-2xl font-bold text-gray-800 dark:text-gray-100"
                  >
                    {{ resultado()!.totalFilas }}
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    Total filas
                  </p>
                </div>
                <div class="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <p class="text-2xl font-bold text-green-600">
                    {{ resultado()!.filasExitosas }}
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    Exitosas
                  </p>
                </div>
                <div class="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <p class="text-2xl font-bold text-red-600">
                    {{ resultado()!.filasConError }}
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    Con errores
                  </p>
                </div>
              </div>
            </div>

            <!-- Filtros de la tabla de resultados -->
            <div class="flex flex-wrap gap-2 mb-2">
              <p-button
                [label]="'Todos (' + resultado()!.detalles.length + ')'"
                [severity]="tabActivo() === 'todos' ? 'primary' : 'secondary'"
                [outlined]="tabActivo() !== 'todos'"
                size="small"
                styleClass="min-h-[44px]"
                (onClick)="tabActivo.set('todos')"
              ></p-button>
              <p-button
                [label]="'Exitosos (' + resultado()!.filasExitosas + ')'"
                [severity]="
                  tabActivo() === 'exitosos' ? 'success' : 'secondary'
                "
                [outlined]="tabActivo() !== 'exitosos'"
                size="small"
                styleClass="min-h-[44px]"
                (onClick)="tabActivo.set('exitosos')"
              ></p-button>
              <p-button
                [label]="'Con errores (' + resultado()!.filasConError + ')'"
                [severity]="tabActivo() === 'errores' ? 'danger' : 'secondary'"
                [outlined]="tabActivo() !== 'errores'"
                size="small"
                styleClass="min-h-[44px]"
                (onClick)="tabActivo.set('errores')"
              ></p-button>
            </div>

            <!-- Tabla de resultados por fila -->
            <!-- Legacy: CargaArchivoActivity -> getSaRetro() muestra retroalimentación de errores -->
            <div
              class="md:hidden space-y-3 p-3 border rounded border-gray-200 dark:border-gray-700"
            >
              @if (filasVisibles().length === 0) {
                <div class="text-center text-gray-500 dark:text-gray-400 py-4">
                  No hay filas para mostrar
                </div>
              }
              @for (fila of filasVisibles(); track fila.numeroFila) {
                <div
                  class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
                >
                  <div class="mb-3 min-w-0">
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      Fila
                    </div>
                    <div class="font-semibold text-gray-800 dark:text-gray-100">
                      {{ textoTabla(fila.numeroFila) }}
                    </div>
                  </div>
                  <div class="grid grid-cols-1 gap-2 text-sm">
                    <div>
                      <span class="text-gray-500 dark:text-gray-400"
                        >Consecutivo:</span
                      >
                      @if (fila.exitoso && fila.idRegistro) {
                        <span class="ml-1 text-green-600 font-medium">{{
                          textoTabla(fila.idRegistro)
                        }}</span>
                      } @else {
                        <span class="ml-1 text-gray-400">-</span>
                      }
                    </div>
                    <div>
                      <span class="text-gray-500 dark:text-gray-400"
                        >Resultado:</span
                      >
                      @if (!fila.exitoso) {
                        <span class="ml-1 text-red-600">{{
                          textoTabla(
                            fila.mensaje ||
                              (fila.errores && fila.errores.length > 0
                                ? fila.errores.join(', ')
                                : 'Error desconocido')
                          )
                        }}</span>
                      } @else {
                        <span class="ml-1 text-green-600"
                          ><i class="pi pi-check mr-1"></i>Registrado</span
                        >
                      }
                    </div>
                  </div>
                </div>
              }
            </div>

            <div class="hidden md:block">
              <div
                role="region"
                [attr.aria-label]="resultadosTableConfig().a11y.tableAriaLabel"
                class="sr-only"
              ></div>
              <p-table
                [resizableColumns]="true"
                columnResizeMode="expand"
                [value]="filasVisibles()"
                [scrollable]="true"
                [responsiveLayout]="'scroll'"
                [tableStyle]="{
                  'min-width':
                    resultadosTableConfig().responsive.minWidth || '72rem'
                }"
                [scrollHeight]="
                  resultadosTableConfig().responsive.scrollHeight || '250px'
                "
                [styleClass]="resultadosTableStyleClass()"
              >
                <ng-template pTemplate="header">
                  <tr>
                    <th pResizableColumn class="w-16">
                      <span class="grid-header-text" title="Fila">Fila</span>
                    </th>
                    <th pResizableColumn class="w-32">
                      <span class="grid-header-text" title="Consecutivo"
                        >Consecutivo</span
                      >
                    </th>
                    <th pResizableColumn>
                      <span class="grid-header-text" title="Resultado"
                        >Resultado</span
                      >
                    </th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-fila>
                  <tr>
                    <td class="font-medium">
                      <span
                        class="grid-cell-text"
                        [attr.title]="tituloTabla(fila.numeroFila)"
                        >{{ textoTabla(fila.numeroFila) }}</span
                      >
                    </td>
                    <td>
                      @if (fila.exitoso && fila.idRegistro) {
                        <span
                          class="grid-cell-text text-green-600 font-medium"
                          [attr.title]="tituloTabla(fila.idRegistro)"
                        >
                          {{ textoTabla(fila.idRegistro) }}
                        </span>
                      } @else {
                        <span
                          class="grid-cell-text text-gray-400"
                          [attr.title]="tituloTabla('-')"
                          >-</span
                        >
                      }
                    </td>
                    <td>
                      @if (!fila.exitoso) {
                        <span
                          class="grid-cell-text text-red-600"
                          [attr.title]="
                            tituloTabla(
                              fila.mensaje ||
                                (fila.errores && fila.errores.length > 0
                                  ? fila.errores.join(', ')
                                  : 'Error desconocido')
                            )
                          "
                        >
                          {{
                            textoTabla(
                              fila.mensaje ||
                                (fila.errores && fila.errores.length > 0
                                  ? fila.errores.join(', ')
                                  : 'Error desconocido')
                            )
                          }}
                        </span>
                      } @else {
                        <span
                          class="grid-cell-text text-green-600"
                          [attr.title]="tituloTabla('Registrado')"
                        >
                          <i class="pi pi-check mr-1"></i>Registrado
                        </span>
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td
                      colspan="3"
                      class="text-center text-gray-500 dark:text-gray-400 py-4"
                    >
                      No hay filas para mostrar
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </div>
          </div>
        }

      }

      <!-- Estado: Procesando -->
      @if (estado() === 'procesando') {
        <div class="text-center py-8">
          <i class="pi pi-spin pi-spinner text-5xl text-blue-500 mb-4"></i>
          <p class="text-lg font-semibold dark:text-gray-100">
            Procesando archivo...
          </p>
          <p class="text-gray-500 dark:text-gray-400">
            El servidor está validando y registrando los cumplimientos
          </p>
          <p-progressBar [mode]="'indeterminate'" styleClass="mt-4 h-2">
          </p-progressBar>
        </div>
      }

      <ng-template pTemplate="footer">
        @if (estado() === 'cargar') {
          <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
            @if (resultado()) {
              <p-button
                label="Cerrar"
                [outlined]="true"
                severity="secondary"
                styleClass="min-h-[44px]"
                (onClick)="cerrar()"
              ></p-button>
              <p-button
                label="Limpiar"
                icon="pi pi-eraser"
                severity="secondary"
                [outlined]="true"
                styleClass="min-h-[44px]"
                (onClick)="reiniciar()"
              ></p-button>
              @if (resultado()!.filasConError > 0) {
                <p-button
                  label="Descargar errores"
                  icon="pi pi-download"
                  severity="warn"
                  [outlined]="true"
                  [loading]="descargandoErrores()"
                  styleClass="min-h-[44px]"
                  pTooltip="Descarga un Excel con las filas que fallaron"
                  (onClick)="descargarErrores()"
                ></p-button>
              }
            } @else {
              <p-button
                label="Cerrar"
                [outlined]="true"
                severity="secondary"
                styleClass="min-h-[44px]"
                (onClick)="cerrar()"
              ></p-button>
              <p-button
                label="Procesar"
                icon="pi pi-upload"
                [disabled]="!archivoSeleccionado()"
                [loading]="procesando()"
                styleClass="min-h-[44px]"
                (onClick)="procesar()"
              ></p-button>
            }
          </div>
        }
      </ng-template>
    </p-dialog>
  `,
  styles: []
})
export class CumplimientosMasivasDialogComponent {
  private readonly cargaMasivaRepository = inject(CargaMasivaRepository);

  @ViewChild('fileUploader') fileUploader!: FileUploaderComponent;

  // === Inputs / Outputs ===

  /** Controla la visibilidad del diálogo (desde el padre) */
  visible = input<boolean>(false);

  /** Señal interna sincronizada con el input */
  dialogVisible = signal(false);

  /** Emite al cerrar el diálogo */
  onClose = output<void>();

  /** Emite cuando se procesó al menos un registro exitoso */
  onProcesamientoExitoso = output<CargaMasivaResumen>();

  constructor() {
    // Sincroniza el input con la señal interna para controlar p-dialog
    effect(() => {
      this.dialogVisible.set(this.visible());
    });
  }

  // === Signals ===

  /** Estado actual: 'cargar' | 'procesando' */
  estado = signal<'cargar' | 'procesando'>('cargar');

  /** Indica si hay una operación en curso (deshabilita cierre del diálogo) */
  procesando = signal(false);

  /** Archivo seleccionado por el usuario */
  archivoSeleccionado = signal<File | null>(null);

  /** Resultado del backend tras el procesamiento */
  resultado = signal<CargaMasivaResumen | null>(null);

  /** Indica si hay una descarga de errores en curso */
  descargandoErrores = signal(false);

  /** Mensaje de error a mostrar */
  error = signal<string | null>(null);

  /** Pestaña activa en la tabla de resultados */
  tabActivo = signal<'todos' | 'exitosos' | 'errores'>('todos');

  readonly resultadosTableConfig = computed(() =>
    cumplimientosMasivosResultadoPreset.createConfig({
      data: this.filasVisibles() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-cumplimientos-masivos',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.procesando(),
        emptyMessage: 'No hay filas para mostrar'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '250px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de resultados de cumplimientos masivos',
        announceSelection: false,
        announcePageChanges: false,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly resultadosTableStyleClass = computed(() => {
    const config = this.resultadosTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // === Computed ===

  /** Filas filtradas según la pestaña activa */
  filasVisibles = computed((): FilaResultado[] => {
    const detalles = this.resultado()?.detalles ?? [];
    switch (this.tabActivo()) {
      case 'exitosos':
        return detalles.filter((f) => f.exitoso);
      case 'errores':
        return detalles.filter((f) => !f.exitoso);
      default:
        return detalles;
    }
  });

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  // === Configuración ===

  /**
   * URL de descarga de la plantilla CumplimientoSinOC.xls.
   * Legacy: CargaArchivoActivity -> getSaPlantilla() descarga la plantilla
   */
  get plantillaUrl(): string {
    return this.cargaMasivaRepository.getPlantillaUrl('cumplimientos-sin-oc');
  }

  // === Handlers del FileUploader ===

  onFileSelected(event: FileSelectedEvent): void {
    this.archivoSeleccionado.set(event.file);
    this.error.set(null);
    this.resultado.set(null);
  }

  onFileRemoved(): void {
    this.archivoSeleccionado.set(null);
    this.resultado.set(null);
  }

  onFileError(errorMsg: string): void {
    this.error.set(errorMsg);
    this.archivoSeleccionado.set(null);
  }

  // === Acciones ===

  /**
  * Envía el archivo al backend para procesamiento masivo.
   * Legacy: CargaArchivoActivity.processFile() -> command COMMAND_PROCESS_EXCEL
   *         con ClaseLectorExcel.LECTOR_CUMPLIMIENTOS_SINOC
   */
  procesar(): void {
    const archivo = this.archivoSeleccionado();
    if (!archivo) {
      this.error.set('No hay archivo seleccionado');
      return;
    }

    this.estado.set('procesando');
    this.procesando.set(true);
    this.error.set(null);

    this.cargaMasivaRepository.procesarCumplimientosMasivos(archivo).subscribe({
      next: (resumen) => {
        this.resultado.set(resumen);
        this.estado.set('cargar');
        this.procesando.set(false);
        if (resumen.filasExitosas > 0) {
          this.onProcesamientoExitoso.emit(resumen);
        }
      },
      error: (err: { error?: { message?: string }; message?: string }) => {
        this.error.set(
          err.error?.message ?? err.message ?? 'Error al procesar el archivo'
        );
        this.estado.set('cargar');
        this.procesando.set(false);
      }
    });
  }

  /** Reinicia el diálogo para cargar otro archivo */
  reiniciar(): void {
    this.estado.set('cargar');
    this.archivoSeleccionado.set(null);
    this.resultado.set(null);
    this.error.set(null);
    this.tabActivo.set('todos');
    this.descargandoErrores.set(false);
    this.fileUploader?.clear();
  }

  /** Maneja el cambio de visibilidad emitido por p-dialog */
  onDialogVisibleChange(visible: boolean): void {
    if (!visible && !this.procesando()) {
      this.cerrar();
    }
  }

  /** Cierra el diálogo y emite el evento onClose */
  cerrar(): void {
    if (this.procesando()) return;
    this.dialogVisible.set(false);
    this.reiniciar();
    this.onClose.emit();
  }

  /**
   * Descarga un Excel con las filas que fallaron.
   *
  * Envía la lista de filas con error al backend (POST /cumplimientos-sin-oc/errores)
   * y dispara la descarga del archivo en el navegador.
   */
  descargarErrores(): void {
    const filasConError =
      this.resultado()?.detalles.filter((f) => !f.exitoso) ?? [];
    if (filasConError.length === 0) return;

    this.descargandoErrores.set(true);
    this.cargaMasivaRepository
      .descargarErroresCumplimientos(filasConError)
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'ErroresCumplimientosSinOC.xlsx';
          link.click();
          URL.revokeObjectURL(url);
          this.descargandoErrores.set(false);
        },
        error: () => {
          this.error.set('No se pudo descargar el archivo de errores');
          this.descargandoErrores.set(false);
        }
      });
  }
}
